import {
  createContext,
  useContext,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ExceptionSeverity, PeriodStatus, TimesheetStatus } from '@em/shared';
import { errorDetails, errorMessage } from '../api';
import { fmtHM, hrs, PERIOD_STATUS, SEVERITY, TIMESHEET_STATUS, type Tone } from './format';

/* ---------------------------------------------------------------- Button */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'primary' | 'danger' | 'danger-solid' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  block?: boolean;
};
export function Button({ variant = 'default', size = 'md', loading, block, className, children, disabled, type = 'button', ...rest }: ButtonProps) {
  const cls = ['btn', variant === 'danger-solid' ? 'danger solid' : variant !== 'default' ? variant : '', size !== 'md' ? size : '', block ? 'block' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <button type={type} className={cls} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading && <span className="spinner" aria-hidden />}
      {children}
    </button>
  );
}

/* ---------------------------------------------------------------- Badges */

export function Badge({ tone = 'grey', children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  return (
    <span className={`badge ${tone === 'grey' ? '' : tone}`} title={title}>
      {children}
    </span>
  );
}
export const TimesheetBadge = ({ status }: { status: TimesheetStatus }) => <Badge tone={TIMESHEET_STATUS[status].tone}>{TIMESHEET_STATUS[status].label}</Badge>;
export const PeriodBadge = ({ status }: { status: PeriodStatus }) => <Badge tone={PERIOD_STATUS[status].tone}>{PERIOD_STATUS[status].label}</Badge>;
export const SeverityBadge = ({ severity }: { severity: ExceptionSeverity }) => <Badge tone={SEVERITY[severity].tone}>{SEVERITY[severity].label}</Badge>;

/** Decimal hours with h:mm on hover. */
export function Hours({ value, className }: { value: number | null | undefined; className?: string }) {
  if (value == null) return <span className={className}>–</span>;
  return (
    <span className={className} title={`${fmtHM(value)} (h:mm)`}>
      {hrs(value)}
    </span>
  );
}

/* ---------------------------------------------------------------- Fields */

const FieldCtx = createContext<{ id: string; describedBy?: string; invalid: boolean } | null>(null);

export function Field({ label, hint, error, children, className, required }: { label: ReactNode; hint?: ReactNode; error?: string | null; children: ReactNode; className?: string; required?: boolean }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  return (
    <FieldCtx.Provider value={{ id, describedBy: [hintId, errId].filter(Boolean).join(' ') || undefined, invalid: !!error }}>
      <div className={`field ${className ?? ''}`}>
        <label htmlFor={id}>
          {label}
          {required && <span aria-hidden> *</span>}
        </label>
        {children}
        {hint && (
          <span className="hint" id={hintId}>
            {hint}
          </span>
        )}
        {error && (
          <span className="error" id={errId} role="alert">
            {error}
          </span>
        )}
      </div>
    </FieldCtx.Provider>
  );
}
function useFieldProps(id?: string) {
  const f = useContext(FieldCtx);
  return { id: id ?? f?.id, 'aria-describedby': f?.describedBy, 'aria-invalid': f?.invalid || undefined };
}

export function Input({ className, id, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`input ${className ?? ''}`} {...useFieldProps(id)} {...rest} />;
}
export function Textarea({ className, id, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`textarea ${className ?? ''}`} {...useFieldProps(id)} {...rest} />;
}
export function Select({ className, id, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={`select ${className ?? ''}`} {...useFieldProps(id)} {...rest}>
      {children}
    </select>
  );
}
export function Checkbox({ label, checked, onChange, disabled }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

/* ---------------------------------------------------------------- Tabs */

export function Tabs<T extends string>({ items, value, onChange, label }: { items: { value: T; label: ReactNode }[]; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {items.map((it) => (
        <button key={it.value} role="tab" aria-selected={it.value === value} onClick={() => onChange(it.value)}>
          {it.label}
        </button>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- Layout bits */

export function PageHeader({ title, subtitle, actions, breadcrumb }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; breadcrumb?: ReactNode }) {
  return (
    <div className="page-header">
      <div className="titles">
        {breadcrumb && <div className="breadcrumb">{breadcrumb}</div>}
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function Card({ title, actions, children, tight, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; tight?: boolean; className?: string }) {
  return (
    <section className={`card ${className ?? ''}`}>
      {(title || actions) && (
        <div className="card-header">
          {typeof title === 'string' ? <h2>{title}</h2> : <div style={{ flex: 1 }}>{title}</div>}
          {actions}
        </div>
      )}
      <div className={`card-body ${tight ? 'tight' : ''}`}>{children}</div>
    </section>
  );
}

/* ---------------------------------------------------------------- States */

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <span className="spinner" aria-hidden /> {label}…
    </div>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {children && <div>{children}</div>}
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

export function ErrorAlert({ error, title }: { error: unknown; title?: string }) {
  if (!error) return null;
  const details = errorDetails(error);
  return (
    <div className="alert red" role="alert">
      <div className="alert-title">{title ?? errorMessage(error)}</div>
      {title && <div>{errorMessage(error)}</div>}
      {details.length > 0 && (
        <ul>
          {details.map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Alert({ tone = 'grey', title, children }: { tone?: Tone; title?: ReactNode; children?: ReactNode }) {
  return (
    <div className={`alert ${tone}`} role={tone === 'red' ? 'alert' : undefined}>
      {title && <div className="alert-title">{title}</div>}
      {children}
    </div>
  );
}

/** Renders loading and error states for a query, then the children with data. */
export function QueryState<T>({ q, children, loadingLabel }: { q: UseQueryResult<T>; children: (data: T) => ReactNode; loadingLabel?: string }) {
  if (q.isPending) return <Loading label={loadingLabel} />;
  if (q.isError)
    return (
      <div className="card-body">
        <ErrorAlert error={q.error} />
        <div style={{ marginTop: 10 }}>
          <Button size="sm" onClick={() => void q.refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  return <>{children(q.data)}</>;
}
